import { icons } from '@/school-management/utils/icons';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSchoolDetails } from '@/school-management/hooks/useSchoolDetails';
import { hasAccessToRoute, ROUTES, ROUTE_WITH_QUERY } from '@/admin/config/routes';

type MenuItem = {
  label: string;
  path: string;
  iconName: keyof typeof icons;
  iconNameActive: keyof typeof icons;
};

type UseMainMenuResult = {
  menuItems: MenuItem[];
  isInSchoolContext: boolean;
  currentSchoolId: string | null;
  backNavigation: {
    label: string;
    path: string;
  } | null;
};

const useMainMenu = (): UseMainMenuResult => {
  const { t } = useAdminTranslation();
  const { userRole, user } = useAppSelector(authSelector);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isOnSchoolDetailsPage =
    location.pathname.startsWith(ROUTES.SCHOOLS + '/') && location.pathname !== ROUTES.SCHOOLS;

  const currentSchoolId = isOnSchoolDetailsPage ? location.pathname.split('/')[2] : null;
  const { data: schoolApiData } = useSchoolDetails(currentSchoolId || undefined);
  const organizationId = user?.metadata?.organizationDetails?.organizationId || currentSchoolId;

  const getDefaultMenuItems = (): MenuItem[] => {
    const menuItems: MenuItem[] = [];

    if (userRole) {
      if (hasAccessToRoute(ROUTES.ACCOUNTS, userRole)) {
        menuItems.push({
          label: t('Accounts'),
          path: ROUTES.ACCOUNTS,
          iconName: 'dealsDefualt',
          iconNameActive: 'deals',
        });
      }

      if (hasAccessToRoute(ROUTES.STATES, userRole)) {
        menuItems.push({
          label: t('States'),
          path: ROUTES.STATES,
          iconName: 'districts',
          iconNameActive: 'Districtactive',
        });
      }

      if (hasAccessToRoute(ROUTES.REGIONS, userRole)) {
        menuItems.push({
          label: t('Regions'),
          path: ROUTES.REGIONS,
          iconName: 'districts',
          iconNameActive: 'Districtactive',
        });
      }

      if (hasAccessToRoute(ROUTES.SCHOOLS, userRole)) {
        menuItems.push({
          label: t('Schools'),
          path: ROUTES.SCHOOLS,
          iconName: 'schools',
          iconNameActive: 'schoolActive',
        });
      }

      if (hasAccessToRoute(ROUTES.HISTORY, userRole)) {
        menuItems.push({
          label: t('History'),
          path: ROUTES.HISTORY,
          iconName: 'history',
          iconNameActive: 'historyActive',
        });
      }

      if (hasAccessToRoute(ROUTES.USER_MANAGEMENT, userRole)) {
        menuItems.push({
          label: t('User Management'),
          path: ROUTES.USER_MANAGEMENT,
          iconName: 'userManagement',
          iconNameActive: 'userManagement',
        });
      }
    }

    return menuItems;
  };

  const getSchoolMenuItems = (schoolId: string): MenuItem[] => {
    return [
      {
        label: t('common.tabs.general'),
        path: ROUTE_WITH_QUERY.SCHOOL_DETAILS_WITH_TAB(schoolId, 'general'),
        iconName: 'dealsDefualt',
        iconNameActive: 'deals',
      },
      {
        label: t('common.tabs.teachers'),
        path: ROUTE_WITH_QUERY.SCHOOL_DETAILS_WITH_TAB(schoolId, 'teachers'),
        iconName: 'districts',
        iconNameActive: 'Districtactive',
      },
      {
        label: t('common.tabs.classes'),
        path: ROUTE_WITH_QUERY.SCHOOL_DETAILS_WITH_TAB(schoolId, 'classes'),
        iconName: 'classes',
        iconNameActive: 'classActive',
      },
      {
        label: t('common.tabs.students'),
        path: ROUTE_WITH_QUERY.SCHOOL_DETAILS_WITH_TAB(schoolId, 'students'),
        iconName: 'schools',
        iconNameActive: 'schoolActive',
      },
    ];
  };

  const getMenuItems = (): MenuItem[] => {
    if (isOnSchoolDetailsPage && currentSchoolId) {
      return getSchoolMenuItems(currentSchoolId);
    }

    return getDefaultMenuItems();
  };

  const menuItems = getMenuItems();

  const getBackNavigation = () => {
    if (isOnSchoolDetailsPage && currentSchoolId) {
      const schoolName = schoolApiData?.data?.data?.school?.name;

      // Only show back navigation if user has access to accounts
      if (userRole && hasAccessToRoute(ROUTES.ACCOUNTS, userRole)) {
        return {
          label: schoolName || '',
          path: ROUTES.ACCOUNTS,
        };
      }

      return null;
    }
    return null;
  };

  return {
    menuItems,
    isInSchoolContext: isOnSchoolDetailsPage,
    currentSchoolId,
    backNavigation: getBackNavigation(),
  };
};

export default useMainMenu;
